import { useCallback, useRef } from 'react';
import {
  Area, AreaChart, CartesianGrid, Legend, ReferenceLine,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';
import type { AppStateShape } from '../../state/appState';
import type { AggregatedSample } from '../../types';
import './PolygonPanel.css';

interface Props {
  state: AppStateShape;
}

export function PolygonPanel({ state }: Props) {
  const { polygonResult, filters, setPolygonResult, setDrawnPolygon, setDrawMode, setHighlightedPointIds } = state;

  const chartRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    setPolygonResult(null);
    setDrawnPolygon(null);
    setDrawMode('NONE');
    setHighlightedPointIds(new Set());
  }, [setPolygonResult, setDrawnPolygon, setDrawMode, setHighlightedPointIds]);

  const handleExportChart = useCallback(() => {
    // Find the recharts SVG and trigger a download
    const svgEl = chartRef.current?.querySelector('svg');
    if (!svgEl) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgEl);
    const blob = new Blob([svgStr], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'polygon_timeseries_chart.svg';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleExportPointsCsv = useCallback(() => {
    if (!polygonResult?.points.length) return;
    const headers = [
      'id_punto',
      'longitud',
      'latitud',
      'velocidad_media_cm_anio',
      'coherencia',
      'rmse_cm',
      'angulo_incidencia_grados',
      'angulo_orbita_grados',
      'direccion_vuelo',
      'altura_m',
    ];
    const rows = polygonResult.points.map((point) => [
      point.point_id,
      point.lon,
      point.lat,
      point.mean_velocity_cm_yr,
      point.coherence,
      point.rmse_cm,
      point.incidence_angle_deg,
      point.track_angle_deg,
      point.flight_direction,
      point.height_m,
    ]);
    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map(csvCell).join(',')),
    ].join('\n');
    downloadText(csv, `nube_puntos_poligono_${polygonResult.component.toLowerCase()}.csv`, 'text/csv;charset=utf-8');
  }, [polygonResult]);

  if (!polygonResult) return null;

  const { samples, n_points, polygon_area_km2, mean_velocity_cm_yr, component, points } = polygonResult;
  const isRasterStats = points.length === 0 && samples.length <= 1;
  const sampleLabel = isRasterStats ? 'píxeles ráster' : 'puntos radar';
  const panelLabel = isRasterStats ? 'Estadísticas ráster del polígono' : 'Serie temporal agregada del polígono';
  const yUnit = isRasterStats ? 'cm/yr' : 'cm';

  const chartData = interpolateSamples(samples);

  const velClass = mean_velocity_cm_yr < -0.3 ? 'v-neg' : mean_velocity_cm_yr > 0.3 ? 'v-pos' : 'v-stable';

  return (
    <div className="poly-panel">
      {/* Header */}
      <div className="poly-panel-header">
        <div className="poly-panel-title">
          <span className="poly-panel-icon">◆</span>
          <div>
            <div className="poly-panel-label">{panelLabel}</div>
            <div className="poly-panel-sub">
              {component} · {n_points} {sampleLabel} · {polygon_area_km2.toFixed(2)} km²
              · {filters.startDate} → {filters.endDate}
            </div>
          </div>
        </div>

        <div className="poly-panel-stats">
          <StatChip label="Velocidad media" value={`${mean_velocity_cm_yr > 0 ? '+' : ''}${mean_velocity_cm_yr.toFixed(2)} cm/yr`} className={velClass} />
          <StatChip label={isRasterStats ? 'Muestras' : 'Muestras temporales'} value={`${samples.length}`} />
          <StatChip label={isRasterStats ? 'Píxeles' : 'Puntos'} value={`${n_points}`} />
        </div>

        <div className="poly-panel-actions">
          <button
            id="btn-export-points-csv"
            className="poly-btn"
            onClick={handleExportPointsCsv}
            disabled={points.length === 0}
            title="Exportar nube de puntos como CSV"
          >
            ↓ CSV
          </button>
          <button
            id="btn-export-chart-svg"
            className="poly-btn"
            onClick={handleExportChart}
            title="Exportar gráfico como SVG"
          >
            ↓ SVG
          </button>
          <button
            className="poly-btn poly-btn-close"
            onClick={handleClose}
            aria-label="Cerrar panel de polígono"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Chart */}
      {samples.length === 0 ? (
        <div className="poly-empty">
          No se encontraron píxeles ráster válidos dentro del polígono. Intenta dibujar dentro de la capa de desplazamiento cargada.
        </div>
      ) : (
        <div className="poly-chart-wrap" ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 30, bottom: 20, left: 20 }}>
              <defs>
                <linearGradient id="bandFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4cc2d4" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#4cc2d4" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1f2a33" strokeDasharray="2 3" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#8896a1', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                stroke="#2a3843"
                tickMargin={6}
                minTickGap={60}
              />
              <YAxis
                tick={{ fill: '#8896a1', fontSize: 10, fontFamily: 'JetBrains Mono' }}
                stroke="#2a3843"
                tickMargin={4}
                label={{
                  value: yUnit,
                  position: 'insideLeft',
                  angle: -90,
                  offset: 8,
                  fill: '#5b6874',
                  fontSize: 10,
                }}
              />
              <ReferenceLine y={0} stroke="#3a5867" strokeDasharray="3 3" />
              <RTooltip content={<PolygonTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 11, color: '#8896a1', paddingTop: 4 }}
                iconType="line"
              />

              {/* ±1σ band */}
              <Area
                type="monotone"
                dataKey="upper"
                name="Superior (media+σ)"
                stroke="none"
                fill="url(#bandFill)"
                fillOpacity={1}
                dot={false}
                legendType="none"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="lower"
                name="Inferior (media−σ)"
                stroke="none"
                fill="var(--bg-1)"
                fillOpacity={1}
                dot={false}
                legendType="none"
                isAnimationActive={false}
              />

              {/* Mean signal */}
              <Area
                type="monotone"
                dataKey="mean"
                name={isRasterStats ? 'Tasa media del polígono' : 'Media del polígono interpolada'}
                stroke="#4cc2d4"
                strokeWidth={2}
                fill="none"
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, className = '' }: { label: string; value: string; className?: string }) {
  return (
    <div className="stat-chip">
      <span className="stat-label">{label}</span>
      <span className={`stat-value ${className}`}>{value}</span>
    </div>
  );
}

function csvCell(value: string | number): string {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadText(content: string, filename: string, type: string): void {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function interpolateSamples(samples: AggregatedSample[]) {
  if (samples.length <= 1) {
    return samples.map((s) => toChartSample(s, false));
  }
  const out: Array<{
    date: string;
    mean: number;
    upper: number;
    lower: number;
    n: number;
    interpolated: boolean;
  }> = [];
  const points = samples.map((s) => ({
    raw: s,
    time: new Date(`${s.date}T00:00:00`).getTime(),
  }));
  const revisitDays = 12;
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];
    if (i === 0) out.push(toChartSample(a.raw, false));
    const gapDays = Math.max(1, Math.round((b.time - a.time) / (24 * 60 * 60 * 1000)));
    const steps = Math.max(2, Math.round(gapDays / revisitDays));
    for (let step = 1; step < steps; step += 1) {
      const t = step / steps;
      const date = new Date(a.time + (b.time - a.time) * t).toISOString().slice(0, 10);
      const mean = lerp(a.raw.mean_displacement_cm, b.raw.mean_displacement_cm, smooth(t));
      const std = lerp(a.raw.std_displacement_cm, b.raw.std_displacement_cm, smooth(t));
      out.push({
        date,
        mean,
        upper: mean + std,
        lower: mean - std,
        n: Math.round(lerp(a.raw.n_points, b.raw.n_points, t)),
        interpolated: true,
      });
    }
    out.push(toChartSample(b.raw, false));
  }
  return out;
}

function toChartSample(s: AggregatedSample, interpolated: boolean) {
  return {
    date: s.date,
    mean: s.mean_displacement_cm,
    upper: s.mean_displacement_cm + s.std_displacement_cm,
    lower: s.mean_displacement_cm - s.std_displacement_cm,
    n: s.n_points,
    interpolated,
  };
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

interface TTPayload {
  date?: string;
  mean?: number;
  upper?: number;
  lower?: number;
  n?: number;
  interpolated?: boolean;
}

function PolygonTooltip({ active, payload }: { active?: boolean; payload?: { payload: TTPayload }[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="poly-tooltip">
      <div className="poly-tooltip-date">{d.date}</div>
      {d.interpolated && (
        <div className="poly-tooltip-row">
          <span>tipo</span>
          <strong>interpolado</strong>
        </div>
      )}
      <div className="poly-tooltip-row">
        <span>media</span>
        <strong>{(d.mean ?? 0).toFixed(2)}</strong>
      </div>
      <div className="poly-tooltip-row">
        <span>+σ</span>
        <strong>{(d.upper ?? 0).toFixed(2)}</strong>
      </div>
      <div className="poly-tooltip-row">
        <span>−σ</span>
        <strong>{(d.lower ?? 0).toFixed(2)}</strong>
      </div>
      <div className="poly-tooltip-row">
        <span>n pts</span>
        <strong>{d.n}</strong>
      </div>
    </div>
  );
}
